import { useMemo, useRef, useState } from 'react';
import ReactModal from 'react-modal';
import { Attachment } from 'presentation/components/Queue/Chat/Chat';
import { IconClear, IconDownload } from 'components/Icons';
import MessageAttachmentPreview from '../MessageAttachmentPreview';

import './MessageAttachmentModal.scss';
import { cleanFilename } from 'presentation/components/Queue/utils/fileUtils';

type MessageAttachmentModalProps = {
  attachments: Attachment[] | undefined;
  handleClose: () => void;
};

const MessageAttachmentModal: React.FC<MessageAttachmentModalProps> = ({ attachments, handleClose }) => {
  const modalRef = useRef(null);
  if (!attachments) return null;
  const attachment = attachments && attachments[0];

  const [currentAttachment, setCurrentAttachment] = useState<Attachment>(attachment);
  const [currentBlob, setCurrentBlob] = useState<Blob | undefined>(undefined);

  const cleanedFilename = useMemo(() => {
    return cleanFilename(currentAttachment.filename);
  }, [currentAttachment]);

  const handleDownload = () => {
    if (!currentBlob) return;

    try {
      const url = URL.createObjectURL(currentBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = cleanedFilename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Download failed:', e);
      alert('Failed to download file: ' + (e instanceof Error ? e.message : 'Unknown error'));
    }
  };

  const handleChangeImage = (attachment: Attachment) => {
    setCurrentAttachment(attachment);
  };

  const handleChangeBlob = (blob: Blob) => {
    setCurrentBlob(blob);
  };

  const attachmentModalStyle = {
    content: {
      top: '5%',
      left: '25%',
      width: '60%',
    },
  };

  return (
    <ReactModal
      ref={modalRef}
      id="attachment-modal"
      style={attachmentModalStyle}
      isOpen={true}
      onRequestClose={handleClose}
      ariaHideApp={false}
    >
      <div id="attachment-modal" className="attachment-modal">
        <div className="attachment-modal__overlay"></div>
        <div className="attachment-modal__content">
          <div className="attachment-modal__content--container">
            <div className="attachment-modal__content--header">
              <h2 className="attachment-modal__content--title">{cleanedFilename}</h2>
              <div className="attachment-modal__content--actions">
                <button
                  className="attachment-modal__content--actions--button attachment-modal__content--actions--download"
                  onClick={handleDownload}
                >
                  <IconDownload />
                  Download
                </button>
                <button
                  className="attachment-modal__content--actions--button attachment-modal__content--actions--close"
                  onClick={handleClose}
                >
                  <IconClear />
                </button>
              </div>
            </div>
            <div className="attachment-modal__content--viewer">
              <MessageAttachmentPreview attachment={currentAttachment} size="normal" />
            </div>
          </div>
        </div>
        <div className="attachment-modal--footer">
          <div className="attachment-modal--footer--preview-thumbnails">
            {attachments.map((attachment, index) => (
              <MessageAttachmentPreview
                attachment={attachment}
                size="thumbnail"
                handleClickPreview={handleChangeImage}
                handleSetDownloadedBlob={handleChangeBlob}
                isSelected={attachment.filename === currentAttachment.filename}
                key={index}
              />
            ))}
          </div>
        </div>
      </div>
    </ReactModal>
  );
};

export default MessageAttachmentModal;
